import { ArrowLeft, Save } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { controlesDe } from '@/api/client';
import { useActionsConge, useAgents, useCalculConge, useConge } from '@/api/hooks';
import { CATEGORIES_CONGE, LIBELLE_CATEGORIE_CONGE, LIBELLE_SOUS_MOTIF, type CategorieConge, type CongeSaisie, type SousMotifCirconstance } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { BlocSolde, ControlesConge, EtapesPrevues } from '@/components/rh';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';
import { dateFr } from '@/lib/utils';

const SOUS_MOTIFS = Object.keys(LIBELLE_SOUS_MOTIF) as SousMotifCirconstance[];

/** Formulaire de demande de congé (§ 4.4) : création, modification d'un brouillon, nouvelle version d'un congé approuvé. */
export function CongeFormulaire() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const nouvelleVersion = pathname.endsWith('/nouvelle-version');
  const existant = useConge(id);
  if (id && existant.isLoading) return <Chargement />;
  if (id && (existant.error || !existant.data)) return <ErreurBloc erreur={existant.error} />;
  return <Formulaire key={id ?? 'nouveau'} id={id} base={existant.data?.conge} nouvelleVersion={nouvelleVersion} />;
}

function Formulaire({ id, base, nouvelleVersion }: { id?: string; base?: NonNullable<ReturnType<typeof useConge>['data']>['conge']; nouvelleVersion: boolean }) {
  const navigate = useNavigate();
  const moi = useSession()!.utilisateur;
  const agents = useAgents();
  const actions = useActionsConge();
  // Préremplissage par l'adresse (?dateDebut=&dateReprise=&categorie=) : liens du calendrier et de la documentation.
  const [params] = useSearchParams();
  const dateParam = (cle: string) => (/^\d{4}-\d{2}-\d{2}$/.test(params.get(cle) ?? '') ? params.get(cle)! : '');
  const [v, setV] = useState<CongeSaisie>({
    categorie: base?.categorie && base.categorie !== 'ABSENCE' ? base.categorie : CATEGORIES_CONGE.find((c) => c === params.get('categorie')) ?? 'ANNUEL',
    sousMotif: base?.sousMotif ?? undefined,
    motif: base?.motif ?? '',
    informations: base?.informations ?? {},
    dateDebut: nouvelleVersion ? '' : (base?.dateDebut ?? dateParam('dateDebut')),
    dateReprise: nouvelleVersion ? '' : (base?.dateReprise ?? dateParam('dateReprise')),
    interimaireId: base?.interimaire?.id ?? '',
  });
  const calcul = useCalculConge({ dateDebut: v.dateDebut, dateReprise: v.dateReprise, categorie: v.categorie, sousMotif: v.sousMotif, congeId: id && !nouvelleVersion ? id : nouvelleVersion ? id : undefined });
  const maj = (p: Partial<CongeSaisie>) => setV({ ...v, ...p });
  const infos = (p: Partial<NonNullable<CongeSaisie['informations']>>) => setV({ ...v, informations: { ...v.informations, ...p } });
  const corps = (): CongeSaisie => ({ categorie: v.categorie, sousMotif: v.categorie === 'CIRCONSTANCE' ? v.sousMotif : undefined, motif: v.motif || undefined, informations: Object.fromEntries(Object.entries(v.informations ?? {}).filter(([, x]) => x)) as CongeSaisie['informations'], dateDebut: v.dateDebut, dateReprise: v.dateReprise, interimaireId: v.interimaireId || null });
  const enregistrer = async () => {
    try {
      const r = nouvelleVersion && id ? await actions.nouvelleVersion.mutateAsync({ id, corps: corps() }) : id ? await actions.modifier.mutateAsync({ id, corps: corps() }) : await actions.creer.mutateAsync(corps());
      toast.success(nouvelleVersion ? 'Nouvelle version enregistrée en brouillon : soumettez-la pour la faire valider.' : 'Demande enregistrée en brouillon.');
      navigate(`/rh/conges/${r.conge.id}`);
    } catch (e) {
      const c = controlesDe(e);
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.', { description: c.length ? c.map((x) => x.message).join(' · ') : undefined });
    }
  };
  const restreint = v.categorie === 'MALADIE' || v.categorie === 'ACCOUCHEMENT';
  const titre = nouvelleVersion ? `Nouvelle version de ${base?.numero ?? 'la demande'}` : id ? `Modifier ${base?.numero ?? 'la demande'}` : 'Nouvelle demande de congé';
  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to={id ? `/rh/conges/${id}` : '/rh'} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> {id ? 'Retour à la demande' : 'Mes démarches RH'}
          </Link>
        }
        titre={titre}
        sousTitre={`${moi.prenom} ${moi.nom} · ${moi.fonction} · ${moi.structureCode}${nouvelleVersion ? ' · la version approuvée reste en vigueur jusqu’à la décision' : ''}`}
        actions={
          <Button onClick={enregistrer} disabled={!v.dateDebut || !v.dateReprise || actions.creer.isPending || actions.modifier.isPending || actions.nouvelleVersion.isPending}>
            <Save className="size-4" /> Enregistrer le brouillon
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Motif</CardTitle>
              <CardDescription>Catégories de la fiche de congé. Le congé annuel se déduit du solde ; les autres catégories suivent les règles RH ; l'absence autorisée est un jour non presté, déduit à la paie.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="categorie">Catégorie</Label>
                <SelectNatif id="categorie" value={v.categorie} onChange={(e) => maj({ categorie: e.target.value as CategorieConge, sousMotif: undefined })}>
                  {CATEGORIES_CONGE.map((c) => (
                    <option key={c} value={c}>
                      {LIBELLE_CATEGORIE_CONGE[c]}
                    </option>
                  ))}
                </SelectNatif>
              </div>
              {v.categorie === 'CIRCONSTANCE' && (
                <div className="space-y-1.5">
                  <Label htmlFor="sousMotif">Sous-motif</Label>
                  <SelectNatif id="sousMotif" value={v.sousMotif ?? ''} onChange={(e) => maj({ sousMotif: (e.target.value || undefined) as SousMotifCirconstance | undefined })}>
                    <option value="">Choisir…</option>
                    {SOUS_MOTIFS.map((s) => (
                      <option key={s} value={s}>
                        {LIBELLE_SOUS_MOTIF[s]}
                      </option>
                    ))}
                  </SelectNatif>
                </div>
              )}
              {v.categorie === 'CIRCONSTANCE' && (
                <div className="space-y-1.5">
                  <Label htmlFor="evenement">Date de l'événement</Label>
                  <Input id="evenement" type="date" value={v.informations?.dateEvenement ?? ''} onChange={(e) => infos({ dateEvenement: e.target.value })} />
                </div>
              )}
              {v.categorie === 'MALADIE' && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="cdu">Certificat du</Label>
                    <Input id="cdu" type="date" value={v.informations?.certificatDu ?? ''} onChange={(e) => infos({ certificatDu: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cau">Certificat au</Label>
                    <Input id="cau" type="date" value={v.informations?.certificatAu ?? ''} onChange={(e) => infos({ certificatAu: e.target.value })} />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="praticien">Praticien ou établissement (facultatif)</Label>
                    <Input id="praticien" value={v.informations?.praticien ?? ''} onChange={(e) => infos({ praticien: e.target.value })} />
                  </div>
                </>
              )}
              {v.categorie === 'ACCOUCHEMENT' && (
                <div className="space-y-1.5">
                  <Label htmlFor="accouchement">Date prévue ou réelle de l'accouchement</Label>
                  <Input id="accouchement" type="date" value={v.informations?.dateAccouchement ?? ''} onChange={(e) => infos({ dateAccouchement: e.target.value })} />
                </div>
              )}
              {(v.categorie === 'ABSENCE_AUTORISEE' || v.categorie === 'ANNUEL' || v.categorie === 'CIRCONSTANCE') && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="motif">{v.categorie === 'ABSENCE_AUTORISEE' ? 'Motif (obligatoire)' : 'Commentaire (facultatif)'}</Label>
                  <Textarea id="motif" rows={2} value={v.motif ?? ''} onChange={(e) => maj({ motif: e.target.value })} />
                </div>
              )}
              {restreint && <p className="text-sm text-muted-foreground sm:col-span-2">Le justificatif (certificat ou acte) se joint à la demande après l'enregistrement du brouillon ; il n'est visible que de vous et des Ressources humaines.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Période d'absence et intérim</CardTitle>
              <CardDescription>Début = premier jour non travaillé ; reprise = premier jour travaillé après l'absence. Les jours ouvrables (lundi à vendredi, fériés exclus) se calculent automatiquement.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="debut">Date de début d'absence</Label>
                <Input id="debut" type="date" value={v.dateDebut} onChange={(e) => maj({ dateDebut: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reprise">Date présumée de reprise de service</Label>
                <Input id="reprise" type="date" value={v.dateReprise} onChange={(e) => maj({ dateReprise: e.target.value })} />
              </div>
              <div className="rounded-md border bg-muted/40 p-3 text-sm sm:col-span-2" data-testid="calcul">
                {calcul.data ? (
                  <>
                    <span className="text-2xl font-semibold">{calcul.data.joursOuvrables}</span> jour{calcul.data.joursOuvrables > 1 ? 's' : ''} ouvrable{calcul.data.joursOuvrables > 1 ? 's' : ''} d'absence · dernier jour d'absence le {dateFr(calcul.data.dernierJour)}
                    {calcul.data.feries.length > 0 && <div className="text-muted-foreground">Fériés exclus : {calcul.data.feries.map((f) => `${dateFr(f.date)} (${f.libelle})`).join(', ')}</div>}
                    {calcul.data.avantDouzeMois && <div className="text-amber-700">Congé annuel avant douze mois de service : approbation explicite du directeur et du DRH.</div>}
                  </>
                ) : (
                  <span className="text-muted-foreground">Renseignez les deux dates pour calculer les jours ouvrables.</span>
                )}
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="interimaire">Intérim assuré par</Label>
                <SelectNatif id="interimaire" value={v.interimaireId ?? ''} onChange={(e) => maj({ interimaireId: e.target.value })}>
                  <option value="">Aucun intérimaire désigné</option>
                  {(agents.data ?? [])
                    .filter((a) => a.id !== moi.agentId)
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.prenom} {a.nom} · {a.fonction}
                      </option>
                    ))}
                </SelectNatif>
                <p className="text-xs text-muted-foreground">La désignation d'un intérimaire ne lui transfère aucun droit dans FONAREV 360 ; un intérim applicatif se crée dans l'administration des habilitations.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Solde</CardTitle>
            </CardHeader>
            <CardContent>{calcul.data ? <BlocSolde periode={calcul.data.solde.periode} demandes={calcul.data.solde.demandes} apres={v.categorie === 'ANNUEL' ? calcul.data.solde.apres : calcul.data.solde.periode?.disponible ?? null} titre={v.categorie === 'ANNUEL' ? 'Congé annuel' : 'Hors solde annuel'} /> : <p className="text-sm text-muted-foreground">Le solde s'affiche dès que les dates sont renseignées.</p>}</CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Circuit prévu</CardTitle>
            </CardHeader>
            <CardContent>{calcul.data ? <EtapesPrevues etapes={calcul.data.circuitPrevu} /> : <p className="text-sm text-muted-foreground">—</p>}</CardContent>
          </Card>
          {calcul.data && calcul.data.controles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contrôles</CardTitle>
              </CardHeader>
              <CardContent>
                <ControlesConge controles={calcul.data.controles} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
